/* eslint-disable react/no-unused-state */
import _ from 'lodash';
import React from 'react';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { BBCommonProps, StoryModel } from 'types/story';
import { StoryFacade } from 'common/utils';
import { isLayerType } from 'common/utils/is-layer-type';
import { triggerFocus } from 'common/utils/helpers';
import { COMPONENT_STATES, UNIFORM_PROPS } from 'common/constants';
import { ClientReducerState } from 'client/reducers';
import { transmitToAdminBbUpdate } from 'client/utils/transmit-to-admin-bb-update';
import { CHILDREN_KEY } from 'client/components/common/BuildingBlocks/utils/common';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import { FIELD_EDITABLE_ATTR } from 'client/components/common/BuildingBlocks/Fields/constants';
import css from './Text.scss';
import ContentEditable from './ContentEditable';

type State = {
	html: string;
	editing: boolean;
};

type Props = BBCommonProps & { children?: string };

class Text extends React.Component<Props, State> {
	static defaultProps = {
		editableModeProps: {},
		children: undefined,
		in: undefined,
	};

	editableRef = React.createRef<HTMLElement>();

	constructor(props, context) {
		super(props, context);

		this.state = {
			html: props.children,
			editing: false,
		};
	}

	static getDerivedStateFromProps({ children }: Props, state: State): Partial<State> | null {
		const isUpdatedChildrenFromProp = document.activeElement === document.body && children !== state.html;

		if (isUpdatedChildrenFromProp) {
			return { html: children, editing: false };
		}

		return null;
	}

	onChangeDebounced = _.debounce((getHtml: () => string) => {
		const html = getHtml();
		if (this.state.html !== html) this.save(html, true);
	}, 500);

	onEditComplete = () => {
		this.onChangeDebounced.flush();
		this.disableEditing();
	};

	save = (html: string, isContinueEditing: boolean = false) => {
		const path = this.props.editableModeProps?.nodeProps?.['data-path'];
		const { currentMediaQuery } = this.props;

		if (!path) {
			console.warn("path isn't defined...");
			return;
		}

		// NOTE: children is always writes to defaultState, but for certain media query
		if (isLayerType(this.props).global) {
			transmitToAdminBbUpdate({
				id: Text.name,
				path: `elements.${path}.${CHILDREN_KEY}.${COMPONENT_STATES.DEFAULT}.${currentMediaQuery}`,
				value: html,
				isStory: true,
			});
		} else {
			transmitToAdminBbUpdate({
				id: Text.name,
				path: `elements.${path}.${CHILDREN_KEY}.${COMPONENT_STATES.DEFAULT}.${currentMediaQuery}`,
				value: html,
				isStory: false,
			});
		}

		this.setState({ editing: isContinueEditing, html });
	};

	updateSelectionRect = (p: { getHtml: () => string }) => {
		// trigger SelectionHint update to set current editable node sizes
		triggerFocus(this.editableRef.current);

		this.onChangeDebounced(p.getHtml);
	};

	enableEditing = () => {
		this.setState({ editing: true }, () => {
			// update SelectionHint and place cursor into contenteditable block
			_.defer(() => triggerFocus(this.editableRef.current?.querySelector('[contenteditable="true"]')));
		});
	};

	disableEditing = () => this.setState({ editing: false });

	renderEditable() {
		const { editableModeProps, uiConfig } = this.props;
		const { editing } = this.state;
		const nodeProps = _.merge({}, uiConfig.nodeProps, editableModeProps?.nodeProps);

		const props = {
			...nodeProps,
			...this.props.stateAttrs,
			...this.props.eventListeners,
			ref: this.editableRef,
			className: cn(css.text, css.editable, uiConfig.nodeProps.className),
		};

		if (editing) {
			props[FIELD_EDITABLE_ATTR] = ''; // prevent from dragging by <Points />
			// @ts-expect-error ts-migrate FIXME
			props.onKeyUp = e => e.stopPropagation(); // prevent application keyboard shortcuts while editing text
			// @ts-expect-error ts-migrate FIXME
			props.onKeyDown = e => e.stopPropagation(); // prevent application keyboard shortcuts while editing text
		} else {
			// @ts-expect-error ts-migrate FIXME
			props.onDoubleClick = this.enableEditing;
			// @ts-expect-error ts-migrate FIXME
			props.dangerouslySetInnerHTML = { __html: this.state.html };
		}

		return (
			// @ts-expect-error ts-migrate FIXME
			<div {...props}>
				{editing ? (
					<ContentEditable
						html={this.state.html}
						onChange={this.updateSelectionRect}
						onComplete={this.onEditComplete}
						// @ts-expect-error ts-migrate FIXME
						editableRef={props.ref}
					/>
				) : null}
			</div>
		);
	}

	renderDefault() {
		const { nodeProps, componentProps } = this.props.uiConfig;
		const Component = this.state.html.match(/\$[^0-9][a-z]*/gi) ? DynamicText : DefaultText;

		return (
			<Component
				nodeProps={nodeProps}
				stateAttrs={this.props.stateAttrs}
				eventListeners={this.props.eventListeners}
				tag={componentProps[UNIFORM_PROPS.tag] ?? 'p'}
				html={this.state.html}
				containerRef={this.props.containerRef}
				className={cn(css.text, nodeProps.className)}
			/>
		);
	}

	render() {
		return this.props.isEditableMode ? this.renderEditable() : this.renderDefault();
	}
}

type TextProps = {
	nodeProps: Props['uiConfig']['nodeProps'];
	stateAttrs: Props['stateAttrs'];
	eventListeners: Props['eventListeners'];
	containerRef: Props['containerRef'];
	className: string;
	html: string;
	tag: React.ElementType | string;
};

const DefaultText = ({ tag: Tag, ...props }: TextProps) => (
	<Tag
		{...props.nodeProps}
		{...props.stateAttrs}
		{...props.eventListeners}
		ref={props.containerRef}
		className={cn(css.text, props.nodeProps.className)}
		dangerouslySetInnerHTML={{ __html: props.html }}
	/>
);

const DynamicText = (props: TextProps) => {
	const story = useSelector((state: ClientReducerState) => state.story as StoryModel);
	return <DefaultText {...props} html={new StoryFacade(story).getTransformedDynamicText(props.html)} />;
};

export default withCardTransitionContext(Text);
